export interface ShortcutAction {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  meta?: boolean;
  description: string;
  action: () => void;
}

export interface ShortcutCategory {
  name: string;
  shortcuts: ShortcutAction[];
}

export class ShortcutManager {
  private shortcuts: ShortcutAction[] = [];
  private isEnabled: boolean = true;

  constructor() {
    this.setupGlobalShortcuts();
  }

  private setupGlobalShortcuts() {
    document.addEventListener('keydown', (e) => {
      if (!this.isEnabled) return;
      // Only ignore shortcuts if the target is an input/textarea and NOT the formula bar
      if (
        (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) &&
        !(e.target as HTMLElement).classList.contains('formula-bar-input')
      ) {
        return;
      }
      this.handleKeyDown(e);
    });
  }

  private handleKeyDown(e: KeyboardEvent) {
    const key = e.key.toLowerCase();
    const ctrl = e.ctrlKey;
    const shift = e.shiftKey;
    const alt = e.altKey;
    const meta = e.metaKey;

    // Find matching shortcut
    const shortcut = this.shortcuts.find(s => 
      s.key === key &&
      s.ctrl === ctrl &&
      s.shift === shift &&
      s.alt === alt &&
      s.meta === meta
    );

    if (shortcut) {
      e.preventDefault();
      shortcut.action();
    }
  }

  public addShortcut(shortcut: ShortcutAction) {
    this.shortcuts.push(shortcut);
  }

  public addShortcuts(shortcuts: ShortcutAction[]) {
    this.shortcuts.push(...shortcuts);
  }

  public removeShortcut(key: string, ctrl = false, shift = false, alt = false, meta = false) {
    this.shortcuts = this.shortcuts.filter(s => 
      !(s.key === key && s.ctrl === ctrl && s.shift === shift && s.alt === alt && s.meta === meta)
    );
  }

  public enable() {
    this.isEnabled = true;
  }

  public disable() {
    this.isEnabled = false;
  }

  public getShortcuts(): ShortcutAction[] {
    return [...this.shortcuts];
  }

  public getShortcutsByCategory(): ShortcutCategory[] {
    const categories: { [key: string]: ShortcutAction[] } = {};
    
    this.shortcuts.forEach(shortcut => {
      const category = this.getCategoryForShortcut(shortcut);
      if (!categories[category]) {
        categories[category] = [];
      }
      categories[category].push(shortcut);
    });

    return Object.entries(categories).map(([name, shortcuts]) => ({
      name,
      shortcuts
    }));
  }

  private getCategoryForShortcut(shortcut: ShortcutAction): string {
    const key = shortcut.key;
    
    // File operations
    if (['s', 'o', 'n'].includes(key) && shortcut.ctrl) {
      return 'File Operations';
    }
    
    // Edit operations
    if (['z', 'y', 'x', 'c', 'v', 'a'].includes(key) && shortcut.ctrl) {
      return 'Edit Operations';
    }
    
    // Formatting
    if (['b', 'i', 'u'].includes(key) && shortcut.ctrl) {
      return 'Formatting';
    }
    
    // Navigation
    if (['home', 'end', 'pageup', 'pagedown'].includes(key)) {
      return 'Navigation';
    }
    
    // Cell operations
    if (['f2', 'enter', 'escape', 'tab'].includes(key)) {
      return 'Cell Operations';
    }
    
    // Insert/Delete
    if (['delete', 'backspace'].includes(key)) {
      return 'Insert/Delete';
    }
    
    return 'Other';
  }

  public getShortcutString(shortcut: ShortcutAction): string {
    const parts: string[] = [];
    
    if (shortcut.ctrl) parts.push('Ctrl');
    if (shortcut.shift) parts.push('Shift');
    if (shortcut.alt) parts.push('Alt');
    if (shortcut.meta) parts.push('Cmd');
    
    // Format key display
    let keyDisplay = shortcut.key.toUpperCase();
    if (keyDisplay === ' ') keyDisplay = 'Space';
    if (keyDisplay === 'ARROWUP') keyDisplay = '↑';
    if (keyDisplay === 'ARROWDOWN') keyDisplay = '↓';
    if (keyDisplay === 'ARROWLEFT') keyDisplay = '←';
    if (keyDisplay === 'ARROWRIGHT') keyDisplay = '→';
    
    parts.push(keyDisplay);
    
    return parts.join(' + ');
  }
}

// Predefined shortcuts for Google Sheets-like functionality
export const createDefaultShortcuts = (
  handlers: {
    undo: () => void;
    redo: () => void;
    save: () => void;
    newSheet: () => void;
    openFile: () => void;
    copy: () => void;
    paste: () => void;
    cut: () => void;
    selectAll: () => void;
    bold: () => void;
    italic: () => void;
    underline: () => void;
    insertRow: () => void;
    insertColumn: () => void;
    deleteRow: () => void;
    deleteColumn: () => void;
    find: () => void;
    replace: () => void;
    print: () => void;
    help: () => void;
    zoomIn: () => void;
    zoomOut: () => void;
    zoomReset: () => void;
  }
): ShortcutAction[] => [
  // File Operations
  {
    key: 's',
    ctrl: true,
    description: 'Save',
    action: handlers.save
  },
  {
    key: 'o',
    ctrl: true,
    description: 'Open',
    action: handlers.openFile
  },
  {
    key: 'n',
    ctrl: true,
    description: 'New Sheet',
    action: handlers.newSheet
  },
  {
    key: 'p',
    ctrl: true,
    description: 'Print',
    action: handlers.print
  },

  // Edit Operations
  {
    key: 'z',
    ctrl: true,
    description: 'Undo',
    action: handlers.undo
  },
  {
    key: 'z',
    ctrl: true,
    shift: true,
    description: 'Redo',
    action: handlers.redo
  },
  {
    key: 'y',
    ctrl: true,
    description: 'Redo',
    action: handlers.redo
  },
  {
    key: 'x',
    ctrl: true,
    description: 'Cut',
    action: handlers.cut
  },
  {
    key: 'c',
    ctrl: true,
    description: 'Copy',
    action: handlers.copy
  },
  {
    key: 'v',
    ctrl: true,
    description: 'Paste',
    action: handlers.paste
  },
  {
    key: 'a',
    ctrl: true,
    description: 'Select All',
    action: handlers.selectAll
  },
  {
    key: 'f',
    ctrl: true,
    description: 'Find',
    action: handlers.find
  },
  {
    key: 'h',
    ctrl: true,
    description: 'Replace',
    action: handlers.replace
  },

  // Formatting
  {
    key: 'b',
    ctrl: true,
    description: 'Bold',
    action: handlers.bold
  },
  {
    key: 'i',
    ctrl: true,
    description: 'Italic',
    action: handlers.italic
  },
  {
    key: 'u',
    ctrl: true,
    description: 'Underline',
    action: handlers.underline
  },

  // Insert/Delete
  {
    key: 'insert',
    ctrl: true,
    description: 'Insert Row',
    action: handlers.insertRow
  },
  {
    key: 'insert',
    ctrl: true,
    shift: true,
    description: 'Insert Column',
    action: handlers.insertColumn
  },
  {
    key: 'delete',
    ctrl: true,
    description: 'Delete Row',
    action: handlers.deleteRow
  },
  {
    key: 'delete',
    ctrl: true,
    shift: true,
    description: 'Delete Column',
    action: handlers.deleteColumn
  },

  // Navigation
  {
    key: 'home',
    description: 'Go to beginning of row',
    action: () => {} // Will be handled by grid component
  },
  {
    key: 'end',
    description: 'Go to end of row',
    action: () => {} // Will be handled by grid component
  },
  {
    key: 'home',
    ctrl: true,
    description: 'Go to beginning of sheet',
    action: () => {} // Will be handled by grid component
  },
  {
    key: 'end',
    ctrl: true,
    description: 'Go to end of sheet',
    action: () => {} // Will be handled by grid component
  },
  {
    key: 'pageup',
    description: 'Page Up',
    action: () => {} // Will be handled by grid component
  },
  {
    key: 'pagedown',
    description: 'Page Down',
    action: () => {} // Will be handled by grid component
  },

  // Zoom
  {
    key: '=',
    ctrl: true,
    description: 'Zoom In',
    action: handlers.zoomIn
  },
  {
    key: '-',
    ctrl: true,
    description: 'Zoom Out',
    action: handlers.zoomOut
  },
  {
    key: '0',
    ctrl: true,
    description: 'Reset Zoom',
    action: handlers.zoomReset
  },

  // Help
  {
    key: 'f1',
    description: 'Help',
    action: handlers.help
  },
  {
    key: '/',
    ctrl: true,
    description: 'Help',
    action: handlers.help
  }
];

// Utility function to create shortcut combinations
export const createShortcut = (
  key: string,
  options: {
    ctrl?: boolean;
    shift?: boolean;
    alt?: boolean;
    meta?: boolean;
    description: string;
    action: () => void;
  }
): ShortcutAction => ({
  key,
  ...options
});

// Export singleton instance
export const shortcutManager = new ShortcutManager(); 